export function uid(prefix = 'node') {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
}

export function ratioValue(value = '1:1') {
  const [width, height] = String(value).split(':').map(Number);
  return width > 0 && height > 0 ? width / height : 1;
}

export function ratioString(width, height, fallback = '1:1') {
  const numericWidth = Number(width || 0);
  const numericHeight = Number(height || 0);
  if (!(numericWidth > 0 && numericHeight > 0)) return fallback;
  let left = Math.abs(Math.round(numericWidth));
  let right = Math.abs(Math.round(numericHeight));
  while (right) [left, right] = [right, left % right];
  const divisor = left || 1;
  return `${Math.round(numericWidth / divisor)}:${Math.round(numericHeight / divisor)}`;
}

export function assetRatio(asset, fallback = '1:1') {
  const explicit = String(asset?.ratio || asset?.aspectRatio || '').trim();
  if (/^\d+(?:\.\d+)?:\d+(?:\.\d+)?$/.test(explicit)) return explicit;
  return ratioString(asset?.width, asset?.height, fallback);
}

export function normalizedPreviewSize(ratio = '1:1', area = 108000) {
  const value = Math.max(0.2, Math.min(5, ratioValue(ratio)));
  let width = Math.sqrt(area * value);
  let height = Math.sqrt(area / value);
  // Keep the visual footprint stable across aspect ratios and viewport zoom.
  const max = 440;
  const min = 176;
  if (width > max) {
    height *= max / width;
    width = max;
  }
  if (height > max) {
    width *= max / height;
    height = max;
  }
  if (width < min) {
    height *= min / width;
    width = min;
  }
  if (height < min) {
    width *= min / height;
    height = min;
  }
  return { width: Math.round(width), height: Math.round(height) };
}

export function mediaNodeSize(width = 1, height = 1, kind = 'image') {
  if (kind === 'audio') return { width: 390, height: 126 };
  // Uploaded image/video references share the same normalized visual area as
  // generator previews. Source resolution must never change the canvas footprint.
  const preview = normalizedPreviewSize(`${Math.max(1, width)}:${Math.max(1, height)}`);
  return { width: Math.max(430, preview.width), height: preview.height + 38 };
}

export function generatorPreviewSize(ratio = '16:9') {
  const base = normalizedPreviewSize(ratio);
  return { width: Math.round(base.width * 1.333), height: Math.round(base.height * 1.333) };
}

export function fileKind(file) {
  const type = String(file?.type || '').toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return '';
}

export async function readMediaMetadata(file, kind = fileKind(file)) {
  if (kind === 'image') {
    // The server reads image metadata while creating the optimized canvas preview.
    // Decoding an original 50-100 MP image here can allocate hundreds of MB on the UI thread.
    return { width: 0, height: 0, duration: 0 };
  }
  if (kind === 'video' || kind === 'audio') {
    const url = URL.createObjectURL(file);
    try {
      const element = document.createElement(kind);
      element.preload = 'metadata';
      element.src = url;
      await new Promise((resolve, reject) => {
        element.onloadedmetadata = resolve;
        element.onerror = () => reject(new Error('无法读取媒体信息'));
      });
      return {
        width: kind === 'video' ? Number(element.videoWidth || 0) : 0,
        height: kind === 'video' ? Number(element.videoHeight || 0) : 0,
        duration: Number.isFinite(element.duration) ? element.duration : 0,
      };
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  return { width: 0, height: 0, duration: 0 };
}

export function canvasImagePreviewUrl(url, explicitPreviewUrl = '') {
  if (explicitPreviewUrl) return explicitPreviewUrl;
  const source = String(url || '');
  if (!source || source.startsWith('blob:') || source.startsWith('data:')) return source;
  if (/\/api\/canvas-assets\/[^/?]+\/media\?/i.test(source)) return source.replace('/media?', '/preview?');
  if (/\/api\/media\/tasks\/[^/?]+\/\d+/i.test(source) && !/[?&]preview=1(?:&|$)/i.test(source)) {
    return `${source}${source.includes('?') ? '&' : '?'}preview=1`;
  }
  return source;
}

export function createGeneratorNode(kind, position, config = {}) {
  const isImage = kind === 'image';
  const models = isImage ? (config.models || []) : (config.video?.models || []);
  const model = models[0];
  const ratio = isImage
    ? (config.generation?.defaultRatio || model?.ratios?.[0] || '16:9')
    : (config.video?.defaultRatio || '16:9');
  const videoMinDuration = Math.max(1, Number(model?.minDuration ?? config.video?.minDuration ?? 4));
  const videoMaxDuration = Math.min(60, Math.max(videoMinDuration, Number(model?.maxDuration ?? config.video?.maxDuration ?? 5)));
  const videoAllowedSeconds = (model?.allowedSeconds?.length
    ? model.allowedSeconds
    : Array.from({ length: videoMaxDuration - videoMinDuration + 1 }, (_item, index) => videoMinDuration + index)).map(Number);
  const videoDuration = Math.min(videoMaxDuration, Math.max(videoMinDuration, Number(config.video?.defaultDuration ?? videoMaxDuration)));
  return {
    id: uid(isImage ? 'image-generator' : 'video-generator'),
    type: isImage ? 'imageGenerator' : 'videoGenerator',
    position,
    data: isImage ? {
      title: '图片生成', prompt: '', model: model?.id || '', ratio,
      resolution: config.generation?.defaultResolution || model?.resolutions?.[0] || '2K',
      count: 1,
      taskId: '', status: 'idle', progress: 0, results: [], activeResult: 0,
      createdAt: Date.now(), updatedAt: Date.now(),
    } : {
      title: '视频生成', prompt: '', model: model?.id || '', ratio,
      resolution: model?.defaultResolution || model?.resolutions?.[0] || '720P', duration: videoDuration,
      generationMode: model?.generationMethods?.[0] || '', allowedSeconds: videoAllowedSeconds,
      taskId: '', status: 'idle', progress: 0, videoUrl: '', coverUrl: '',
      createdAt: Date.now(), updatedAt: Date.now(),
    },
  };
}

export function createMediaNode(asset, position) {
  const kind = asset.kind || 'image';
  const ratio = assetRatio(asset, kind === 'video' ? '16:9' : '1:1');
  const [ratioWidth, ratioHeight] = ratio.split(':').map(Number);
  const width = Number(asset.width || 0) > 0 ? Number(asset.width) : Math.max(1, ratioWidth || 1) * 100;
  const height = Number(asset.height || 0) > 0 ? Number(asset.height) : Math.max(1, ratioHeight || 1) * 100;
  return {
    id: uid(`${kind}-asset`),
    type: 'media',
    position,
    data: {
      assetId: asset.id || '', kind, name: asset.name || asset.filename || `${kind} 素材`,
      url: asset.url || '', previewUrl: asset.previewUrl || '', downloadUrl: asset.downloadUrl || '',
      poster: asset.poster || asset.coverUrl || '', mimeType: asset.mimeType || '', width, height, ratio, aspectRatio: ratio,
      duration: Number(asset.duration || 0), size: Number(asset.size || 0),
      createdAt: Date.now(), updatedAt: Date.now(), uploading: false,
    },
  };
}

export function nodeSize(node) {
  if (node.type === 'media') return mediaNodeSize(node.data?.width, node.data?.height, node.data?.kind);
  const preview = generatorPreviewSize(node.data?.ratio || '16:9');
  return { width: Math.max(430, preview.width), height: preview.height + 220 };
}

function compactNodeSize(node) {
  const size = nodeSize(node);
  if (node.type === 'imageGenerator' || node.type === 'videoGenerator') {
    return { ...size, height: Math.max(120, size.height - 220) };
  }
  return size;
}

export function duplicatePosition(node, nodes, gap = 36) {
  const copySize = compactNodeSize(node);
  const others = nodes.filter((item) => item.id !== node.id);
  const candidates = [];
  // Copies read top-to-bottom first. Once the five-slot vertical lane is
  // occupied, continue in a new column on the right instead of jumping a
  // large distance or covering an existing node.
  const sourceSize = compactNodeSize(node);
  const columnStep = sourceSize.width + gap;
  const rowStep = sourceSize.height + gap;
  // The source occupies the first slot, so a column has five total slots.
  for (let row = 1; row <= 4; row += 1) {
    candidates.push({
      x: Math.round(node.position.x),
      y: Math.round(node.position.y + row * rowStep),
    });
  }
  for (let column = 1; column <= Math.ceil((others.length + 1) / 5) + 3; column += 1) {
    for (let row = 0; row < 5; row += 1) {
      candidates.push({
        x: Math.round(node.position.x + column * columnStep),
        y: Math.round(node.position.y + row * rowStep),
      });
    }
  }
  return candidates.find((position) => positionIsOpen(
    others.map((item) => ({ ...item, _placementSize: compactNodeSize(item) })),
    position,
    copySize,
    Math.max(8, gap / 2),
  )) || candidates.at(-1);
}

export function findOpenPosition(nodes, desired, size, gap = 90) {
  const stepX = Math.max(540, Number(size?.width || 430) + gap);
  const stepY = Math.max(520, Number(size?.height || 420) + gap);
  const candidates = [{ x: desired.x, y: desired.y }];
  for (let ring = 1; ring <= 8; ring += 1) {
    for (let column = -ring; column <= ring; column += 1) {
      candidates.push({ x: desired.x + column * stepX, y: desired.y - ring * stepY });
      candidates.push({ x: desired.x + column * stepX, y: desired.y + ring * stepY });
    }
    for (let row = -ring + 1; row < ring; row += 1) {
      candidates.push({ x: desired.x - ring * stepX, y: desired.y + row * stepY });
      candidates.push({ x: desired.x + ring * stepX, y: desired.y + row * stepY });
    }
  }
  const width = Number(size?.width || 430);
  const height = Number(size?.height || 420);
  return candidates.find((candidate) => nodes.every((node) => {
    const existing = nodeSize(node);
    return candidate.x + width + gap <= node.position.x
      || candidate.x >= node.position.x + existing.width + gap
      || candidate.y + height + gap <= node.position.y
      || candidate.y >= node.position.y + existing.height + gap;
  })) || candidates[candidates.length - 1];
}

function positionFitsBounds(position, size, bounds, margin = 18) {
  if (!bounds) return true;
  return position.x >= bounds.x + margin
    && position.y >= bounds.y + margin
    && position.x + size.width <= bounds.x + bounds.width - margin
    && position.y + size.height <= bounds.y + bounds.height - margin;
}

function positionIsOpen(nodes, position, size, gap = 36) {
  return nodes.every((node) => {
    const existing = node._placementSize || nodeSize(node);
    return position.x + size.width + gap <= node.position.x
      || position.x >= node.position.x + existing.width + gap
      || position.y + size.height + gap <= node.position.y
      || position.y >= node.position.y + existing.height + gap;
  });
}

export function findVisiblePosition(nodes, desiredCenter, size, options = {}) {
  const width = Number(size?.width || 430);
  const height = Number(size?.height || 420);
  const bounds = options.bounds || null;
  const anchor = options.anchor || null;
  const gap = Number(options.gap || 48);
  const candidates = [];
  const add = (x, y) => candidates.push({ x: Math.round(x), y: Math.round(y) });

  if (anchor) {
    const anchorSize = nodeSize(anchor);
    const horizontal = {
      x: Math.round(anchor.position.x + anchorSize.width + gap),
      y: Math.round(anchor.position.y),
    };
    for (let pass = 0; pass <= nodes.length; pass += 1) {
      const blockers = nodes.filter((node) => !positionIsOpen([node], horizontal, { width, height }, gap));
      if (!blockers.length) break;
      horizontal.x = Math.round(Math.max(...blockers.map((node) => node.position.x + nodeSize(node).width + gap)));
    }
    return {
      position: horizontal,
      fitsViewport: positionFitsBounds(horizontal, { width, height }, bounds),
    };
  }

  add(desiredCenter.x - width / 2, desiredCenter.y - height / 2);

  if (bounds) {
    const stepX = width + gap;
    const stepY = height + gap;
    const columns = Math.max(1, Math.floor((bounds.width - 36 + gap) / stepX));
    const rows = Math.max(1, Math.floor((bounds.height - 36 + gap) / stepY));
    for (let row = 0; row < rows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        add(bounds.x + 18 + column * stepX, bounds.y + 18 + row * stepY);
      }
    }
  }

  const unique = candidates.filter((candidate, index) => candidates.findIndex((item) => item.x === candidate.x && item.y === candidate.y) === index);
  const visible = unique.find((candidate) => positionFitsBounds(candidate, { width, height }, bounds)
    && positionIsOpen(nodes, candidate, { width, height }, gap));
  if (visible) return { position: visible, fitsViewport: true };

  const open = unique.find((candidate) => positionIsOpen(nodes, candidate, { width, height }, gap));
  if (open) return { position: open, fitsViewport: positionFitsBounds(open, { width, height }, bounds) };

  const fallback = findOpenPosition(nodes, unique[0] || desiredCenter, { width, height }, gap);
  return { position: fallback, fitsViewport: positionFitsBounds(fallback, { width, height }, bounds) };
}

function connectedComponents(nodes, edges) {
  const adjacency = new Map(nodes.map((node) => [node.id, new Set()]));
  edges.forEach((edge) => {
    adjacency.get(edge.source)?.add(edge.target);
    adjacency.get(edge.target)?.add(edge.source);
  });
  const visited = new Set();
  return nodes.map((node) => {
    if (visited.has(node.id)) return null;
    const ids = [];
    const queue = [node.id];
    visited.add(node.id);
    while (queue.length) {
      const current = queue.shift();
      ids.push(current);
      adjacency.get(current)?.forEach((next) => {
        if (!visited.has(next)) { visited.add(next); queue.push(next); }
      });
    }
    return ids;
  }).filter(Boolean);
}

export function arrangeCanvas(nodes, edges, center = { x: 0, y: 0 }) {
  if (!nodes.length) return nodes;
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const incoming = new Map(nodes.map((node) => [node.id, []]));
  edges.forEach((edge) => incoming.get(edge.target)?.push(edge.source));
  const components = connectedComponents(nodes, edges).map((ids) => ({
    ids,
    active: ids.length > 1 || ids.some((id) => byId.get(id)?.type !== 'media'),
    updatedAt: Math.max(...ids.map((id) => Number(byId.get(id)?.data?.updatedAt || 0))),
  })).sort((left, right) => Number(right.active) - Number(left.active) || right.updatedAt - left.updatedAt);

  const positioned = new Map();
  // Arrange each connected workflow as a compact horizontal lane. The old layout
  // used a large fixed x-gap and packed unrelated workflows beside one another,
  // which made a one-source hand-off look disconnected and forced extra panning.
  const originX = center.x - 520;
  let cursorY = center.y - 320;
  let activeBottom = cursorY;
  // Leave a full handle-width of breathing room between stacked nodes. This
  // keeps the glowing connection sockets readable after Arrange, including
  // audio/video lanes whose controls are taller than image cards.
  const stackGap = 96;
  const columnGap = 168;

  for (const component of components.filter((item) => item.active)) {
    const componentNodes = component.ids.map((id) => byId.get(id));
    const generators = componentNodes.filter((node) => node.type !== 'media');
    const generatorsSet = new Set(generators.map((node) => node.id));
    const assets = componentNodes.filter((node) => node.type === 'media');
    const depth = new Map();
    const getDepth = (id, path = new Set()) => {
      if (depth.has(id)) return depth.get(id);
      if (path.has(id)) return 0;
      const nextPath = new Set(path).add(id);
      const sources = (incoming.get(id) || []).filter((sourceId) => generatorsSet.has(sourceId));
      const value = sources.length ? Math.max(...sources.map((sourceId) => getDepth(sourceId, nextPath) + 1)) : 0;
      depth.set(id, value);
      return value;
    };
    generators.forEach((node) => getDepth(node.id));
    const generatorColumns = new Map();
    generators.forEach((node) => {
      const key = getDepth(node.id);
      if (!generatorColumns.has(key)) generatorColumns.set(key, []);
      generatorColumns.get(key).push(node);
    });
    const columnKeys = [...generatorColumns.keys()].sort((a, b) => a - b);
    const sizes = new Map(componentNodes.map((node) => [node.id, compactNodeSize(node)]));
    // Keep one-to-one lanes close enough to read as one unit while giving
    // multi-reference generators additional breathing room. The cap prevents
    // a large reference set from pushing the result off-screen.
    // Keep a full connector-socket width of air between the source lane and
    // the generator. Converging references get additional breathing room.
    const bridgeGap = Math.min(520, 320 + Math.max(0, assets.length - 1) * 48);
    const assetWidth = assets.length ? Math.max(...assets.map((node) => sizes.get(node.id).width)) : 0;
    const assetHeight = assets.reduce((sum, node) => sum + sizes.get(node.id).height, 0) + Math.max(0, assets.length - 1) * stackGap;
    const columnMetrics = columnKeys.map((key) => {
      const column = generatorColumns.get(key);
      return {
        key,
        nodes: column,
        width: Math.max(...column.map((node) => sizes.get(node.id).width)),
        height: column.reduce((sum, node) => sum + sizes.get(node.id).height, 0) + Math.max(0, column.length - 1) * stackGap,
      };
    });
    const generatorHeight = Math.max(0, ...columnMetrics.map((column) => column.height));
    const componentHeight = Math.max(220, assetHeight, generatorHeight);
    const generatorWidth = columnMetrics.reduce((sum, column) => sum + column.width, 0) + Math.max(0, columnMetrics.length - 1) * columnGap;
    const componentWidth = (assets.length ? assetWidth + bridgeGap : 0) + generatorWidth;
    const y = cursorY;
    let assetY = y + (componentHeight - assetHeight) / 2;
    assets.forEach((node) => {
      positioned.set(node.id, { x: originX, y: assetY });
      assetY += sizes.get(node.id).height + stackGap;
    });
    let generatorX = originX + (assets.length ? assetWidth + bridgeGap : 0);
    columnMetrics.forEach((column) => {
      let nodeY = y + (componentHeight - column.height) / 2;
      column.nodes.forEach((node) => {
        positioned.set(node.id, { x: generatorX, y: nodeY });
        nodeY += sizes.get(node.id).height + stackGap;
      });
      generatorX += column.width + columnGap;
    });
    cursorY += componentHeight + 192;
    activeBottom = Math.max(activeBottom, y + componentHeight);
    // Keep every primary lane on the same x origin; only the vertical lane grows.
    // This makes straight one-to-one links visually comparable after Arrange.
    void componentWidth;
  }

  const isolated = components.filter((item) => !item.active).flatMap((item) => item.ids.map((id) => byId.get(id)));
  let isolatedX = originX;
  let isolatedY = activeBottom + (components.some((item) => item.active) ? 192 : 0);
  let isolatedRow = 0;
  isolated.forEach((node) => {
    const size = compactNodeSize(node);
    if (isolatedX + size.width > originX + 1100) {
      isolatedX = originX;
      isolatedY += isolatedRow + 196;
      isolatedRow = 0;
    }
    positioned.set(node.id, { x: isolatedX, y: isolatedY });
    isolatedX += size.width + 96;
    isolatedRow = Math.max(isolatedRow, size.height);
  });

  const grid = 24;
  return nodes.map((node) => {
    const position = positioned.get(node.id) || node.position;
    return {
      ...node,
      position: {
        x: Math.round(Number(position?.x || 0) / grid) * grid,
        y: Math.round(Number(position?.y || 0) / grid) * grid,
      },
    };
  });
}

export function serializableCanvas(nodes, edges, viewport) {
  return {
    nodes: nodes.map((node) => ({ id: node.id, type: node.type, position: node.position, data: node.data })),
    edges: edges.map((edge) => ({ id: edge.id, source: edge.source, target: edge.target, type: 'reference', data: edge.data || {} })),
    viewport,
  };
}
